import React, { useState } from 'react';
import { useSequence } from '../../context/SequenceContext';
import { useCRM } from '../../context/CRMContext';
// For DraftComposer access or modal logic
import SafeIcon from '../../common/SafeIcon';
import DraftComposer from './DraftComposer';
// Reusing existing composer
import BaseModal from '../modals/BaseModal';

const EmailTaskQueue = () => {
  const { tasks, generateDraftFromTask, completeTask } = useSequence();
  const [loadingId, setLoadingId] = useState(null);
  const [activeDraft, setActiveDraft] = useState(null);
  const [isComposerOpen, setIsComposerOpen] = useState(false);

  // Filter only pending email tasks
  const emailTasks = tasks.filter(t => t.type === 'Email' && t.status !== 'Completed');

  const handleGenerate = async (task) => {
    setLoadingId(task.task_id);
    try {
      const draft = await generateDraftFromTask(task.task_id);
      setActiveDraft(draft);
      setIsComposerOpen(true);
      // Open modal with composer
    } catch (e) {
      alert(e.message);
    } finally {
      setLoadingId(null);
    }
  };

  const handleTaskComplete = (taskId) => {
    completeTask(taskId);
    setIsComposerOpen(false);
    setActiveDraft(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-black text-slate-900 text-lg">Dagens E-postoppgaver</h3>
        <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold">{emailTasks.length} klare</span>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {emailTasks.length > 0 ? emailTasks.map(task => (
          <div key={task.task_id} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between group hover:border-primary-300 transition-all">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-primary-50 group-hover:text-primary-600 transition-colors">
                <SafeIcon icon="📧" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900 text-sm">{task.contact_name}</h4>
                <p className="text-xs text-slate-500">{task.company_name} • {task.sequence_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {task.status === 'Generated' ? (
                <button onClick={() => { /* Load existing draft logic */ }} className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-bold border border-emerald-100 hover:bg-emerald-100">
                  Fortsett
                </button>
              ) : (
                <button onClick={() => handleGenerate(task)} disabled={loadingId === task.task_id} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2">
                  {loadingId === task.task_id ? <SafeIcon icon="🔄" className="animate-spin" /> : <SafeIcon icon="✨" />} Generer utkast
                </button>
              )}
            </div>
          </div>
        )) : (
          <div className="p-8 text-center border-2 border-dashed border-slate-200 rounded-2xl text-slate-400">
            Ingen e-postoppgaver forfaller i dag.
          </div>
        )}
      </div>

      {/* Draft Composer Modal */}
      <BaseModal isOpen={isComposerOpen} onClose={() => setIsComposerOpen(false)} title="Send E-post" size="2xl">
        {activeDraft ? (
          <div className="h-[600px]">
            {/* Note: In a real implementation, we would pass the activeDraft to DraftComposer via props or context. Here we assume DraftComposer can pick it up from EmailContext where it was just created. */}
            <DraftComposer />
            <div className="mt-4 flex justify-end">
              <button onClick={() => handleTaskComplete(loadingId)} className="text-xs text-slate-400 hover:text-slate-600 underline">
                Simuler sending & fullfør oppgave (Dev Only)
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4">Laster...</div>
        )}
      </BaseModal>
    </div>
  );
};

export default EmailTaskQueue;